import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../hooks/useAuth';
import logoOQ from '../../assets/images/logo-light.png';
import { InstallPWAiOS } from './InstallPWAiOS';
import { api } from '../../server/api';
import './style.css';

interface InstallPWAProps {
  icon: string;
  name: string;
  text: string;
  client: string;
  color: string;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

export function InstallPWA(props: InstallPWAProps) {
  const { getClientByUrl, client } = useAuth();
  const [pwaState, setPwaState] = useState({
    icon: props.icon,
    name: props.name,
    text: props.text,
    clientUrl: props.client,
    color: props.color,
    supportsPWA: false,
    promptInstall: null as BeforeInstallPromptEvent | null,
    manifestUrl: null as string | null,
    isClose: true,
    isInstalling: false,
    isReady: false,
  });

  function isPWASupported() {
    const supportsBeforeInstallPrompt = 'onbeforeinstallprompt' in window;
    const supportsServiceWorker = 'serviceWorker' in navigator;
    const isHTTPS =
      window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    return supportsBeforeInstallPrompt && supportsServiceWorker && isHTTPS;
  }

  function isPWAInstalled(): boolean {
    const isStandalone = window.matchMedia(
      '(display-mode: standalone)'
    ).matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    const isAndroidApp = document.referrer.includes('android-app://');

    return isStandalone || isIOSStandalone || isAndroidApp;
  }

  function shouldShowInstallPrompt(): boolean {
    if (isPWAInstalled()) {
      return false;
    }

    return true;
  }

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      const beforeInstallEvent = e as BeforeInstallPromptEvent;

      setPwaState((prev) => ({
        ...prev,
        promptInstall: beforeInstallEvent,
        // Only show the modal if it wasn't already dismissed
        isClose: prev.isClose,
      }));
    };

    const displayModeHandler = () => {
      if (isPWAInstalled()) {
        setPwaState((prev) => ({ ...prev, isClose: true }));
      }
    };

    getClientByUrl();

    const pwaSupported = isPWASupported();
    const pwaInstalled = isPWAInstalled();
    const shouldShow = shouldShowInstallPrompt();

    setPwaState((prev) => ({
      ...prev,
      supportsPWA: pwaSupported,
      isClose: !pwaSupported || pwaInstalled || !shouldShow,
      isReady: true,
    }));

    if (pwaSupported && !pwaInstalled) {
      window.addEventListener('beforeinstallprompt', handler);
    }

    window
      .matchMedia('(display-mode: standalone)')
      .addEventListener('change', displayModeHandler);

    return () => {
      if (pwaSupported) {
        window.removeEventListener('beforeinstallprompt', handler);
      }
      window
        .matchMedia('(display-mode: standalone)')
        .removeEventListener('change', displayModeHandler);
    };
  }, []);

  useEffect(() => {
    if (
      pwaState.icon &&
      pwaState.name &&
      pwaState.clientUrl &&
      pwaState.color
    ) {
      const manifest = {
        name: pwaState.name,
        short_name: pwaState.name,
        icons: [
          {
            src: pwaState.icon,
            sizes: '192x192',
            type: 'image/png',
          },
          {
            src: pwaState.icon,
            sizes: '512x512',
            type: 'image/png',
          },
        ],
        theme_color: pwaState.color,
        background_color: pwaState.color,
        start_url: `${window.location.origin}/_/${pwaState.clientUrl}`,
        display: 'standalone',
      };

      const blob = new Blob([JSON.stringify(manifest)], {
        type: 'application/json',
      });
      const manifestUrl = URL.createObjectURL(blob);

      setPwaState((prev) => {
        // Revoke the old blob URL to avoid memory leaks
        if (prev.manifestUrl) {
          URL.revokeObjectURL(prev.manifestUrl);
        }
        return { ...prev, manifestUrl };
      });

      return () => {
        URL.revokeObjectURL(manifestUrl);
      };
    }
  }, [pwaState.icon, pwaState.name, pwaState.clientUrl, pwaState.color]);

  async function install(e: React.MouseEvent) {
    e.preventDefault();
    if (client?.clientId) {
      api
        .post('/update', {
          userType: 'player',
          updateFunctionality: 'increaseInstallPwaCount',
          clientId: client.clientId,
        })
        .catch((err) => console.error('increaseInstallPwaCount error:', err));
    }
    if (!pwaState.promptInstall) {
      alert(
        'Desculpe, a instalação não está disponível no momento. Tente novamente mais tarde.'
      );
      return;
    }

    if (pwaState.isInstalling) {
      return;
    }

    try {
      setPwaState((prev) => ({ ...prev, isInstalling: true }));

      await pwaState.promptInstall.prompt();

      const { outcome } = await pwaState.promptInstall.userChoice;

      if (outcome === 'accepted') {
        localStorage.setItem('pwaInstalled', 'true');
        if (client?.clientId) {
          api
            .post('/update', {
              userType: 'player',
              updateFunctionality: 'increaseInstallPwaCount',
              clientId: client.clientId,
            })
            .catch((err) =>
              console.error('increaseInstallPwaCount error:', err)
            );
        }
        setPwaState((prev) => ({
          ...prev,
          isClose: true,
          isInstalling: false,
          promptInstall: null,
        }));
      } else {
        setPwaState((prev) => ({
          ...prev,
          isClose: true,
          isInstalling: false,
          promptInstall: null,
        }));
      }
    } catch (error) {
      console.error('PWA Installation error:', error);
      setPwaState((prev) => ({
        ...prev,
        isInstalling: false,
      }));
      alert(
        'Erro ao tentar instalar o aplicativo. Por favor, tente novamente.'
      );
    }
  }

  function closeBox(e: React.MouseEvent) {
    e.preventDefault();

    setPwaState((prev) => ({ ...prev, isClose: true }));
  }

  function getInstallMessage() {
    if (pwaState.isInstalling) {
      return 'Aguarde...';
    }
    return 'Instale nosso aplicativo para uma melhor experiência.';
  }

  // CRÍTICO: Não renderiza nada até que a verificação inicial seja concluída
  if (!pwaState.isReady) {
    return null;
  }

  // Se não tem o prompt ou o modal está fechado, renderiza apenas o manifest
  if (!pwaState.promptInstall || pwaState.isClose) {
    return (
      <>
        <Helmet>
          {pwaState.manifestUrl && (
            <link rel="manifest" href={pwaState.manifestUrl} />
          )}
        </Helmet>
        <InstallPWAiOS icon={pwaState.icon} name={pwaState.name} />
      </>
    );
  }

  return (
    <>
      <Helmet>
        {pwaState.manifestUrl && (
          <link rel="manifest" href={pwaState.manifestUrl} />
        )}
      </Helmet>

      <div
        className={`install-pwa-wrapper animate__animated animate__slideInUp`}
      >
        <div className="install-pwa-box">
          <div className="content-header">
            <button
              className="install-pwa-close"
              onClick={closeBox}
              aria-label="Fechar banner de instalação"
              disabled={pwaState.isInstalling}
            >
              Me lembre depois
              <i className="material-icons">close</i>
            </button>
          </div>
          <div className="content-top">
            <div className="box-image">
              <img
                src={pwaState.icon || ''}
                alt={`Ícone do aplicativo ${pwaState.name}`}
              />
            </div>
            <div>
              <p className="truncate">{pwaState.name || ''}</p>
              <div className="content-group-h">
                <img
                  width="16"
                  height="16"
                  style={{ marginRight: '10px' }}
                  src={logoOQ}
                  alt="Logo OQ Digital"
                />
                <span>Powered by OQ Digital</span>
              </div>
              <span>{getInstallMessage()}</span>
            </div>
          </div>
          <div className="content-bottom">
            <div className="content-left">
              <div className="content-group-h">
                <i className="material-icons" style={{ marginRight: '10px' }}>
                  install_mobile
                </i>
                <span style={{ fontSize: '20px' }}>Baixar app</span>
              </div>
            </div>
            <div className="content-right">
              <button
                className="btn-install animate__animated animate__headShake animate__delay-2s"
                onClick={install}
                disabled={pwaState.isInstalling || !pwaState.promptInstall}
                aria-label="Instalar aplicativo"
              >
                {pwaState.isInstalling ? 'Instalando...' : 'Instalar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
